import { Alert, Button, Card, Checkbox, Form, Input, Select, Space, Tag, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type Candidate = {
  staff_link_id: string;
  employee_code?: string;
  first_name?: string;
  last_name?: string;
  teaches_subject?: number | boolean;
  available?: boolean;
};

type Room = { id: string; code: string; name: string };

type Detail = {
  day: { id: string; status: string; absence_date: string };
  covers: Array<{
    id: string;
    status: string;
    period_code?: string;
    subject_code?: string;
    section_name?: string;
    cover_staff_link_id?: string | null;
    cover_room_id?: string | null;
    notes?: string | null;
  }>;
};

type FormValues = {
  cover_staff_link_id: string;
  cover_room_id?: string;
  notes?: string;
  notify: boolean;
};

export default function SubstitutionCoverAssignPage() {
  const { t } = useTranslation('education');
  const { id: dayId, coverId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.substitution.manage') ||
      user?.permissions?.includes('education.admin')
  );
  const [form] = Form.useForm<FormValues>();

  const detail = useQuery({
    queryKey: ['education-substitution-day', dayId],
    enabled: Boolean(dayId),
    queryFn: async () => {
      const res = await api.get<Detail>(`/education/substitutions/${dayId}`);
      return res.data;
    },
  });

  const cover = useMemo(
    () => (detail.data?.covers ?? []).find((c) => c.id === coverId),
    [coverId, detail.data?.covers]
  );

  const candidates = useQuery({
    queryKey: ['education-substitution-candidates', dayId, coverId],
    enabled: Boolean(dayId) && Boolean(coverId) && canManage,
    queryFn: async () => {
      const res = await api.get<Candidate[]>(
        `/education/substitutions/${dayId}/covers/${coverId}/candidates`
      );
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  const rooms = useQuery({
    queryKey: ['education-timetable-rooms'],
    queryFn: async () => {
      const res = await api.get<Room[]>('/education/timetable/rooms');
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  const assign = useMutation({
    mutationFn: (values: FormValues) =>
      api.post(`/education/substitutions/${dayId}/covers/${coverId}/assign`, {
        cover_staff_link_id: values.cover_staff_link_id,
        cover_room_id: values.cover_room_id || null,
        notes: values.notes || null,
        notify: values.notify !== false,
      }),
    onSuccess: () => {
      message.success(t('coverAssigned'));
      queryClient.invalidateQueries({ queryKey: ['education-substitution-day', dayId] });
      navigate(`/education/substitutions/${dayId}`);
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  if (!canManage) {
    return (
      <EducationPageChrome title={t('assignCover')}>
        <Alert type="warning" showIcon message={t('unavailable', { status: 403 })} />
      </EducationPageChrome>
    );
  }

  if (detail.data && (detail.data.day.status === 'closed' || detail.data.day.status === 'cancelled')) {
    return (
      <EducationPageChrome title={t('assignCover')}>
        <Alert type="info" showIcon message={t('substitutionDayLocked')} />
        <Button style={{ marginTop: 12 }} onClick={() => navigate(`/education/substitutions/${dayId}`)}>
          {t('backToSubstitutionDay')}
        </Button>
      </EducationPageChrome>
    );
  }

  return (
    <EducationPageChrome
      title={t('assignCover')}
      description={
        cover
          ? `${cover.period_code || ''} · ${cover.section_name || ''} · ${cover.subject_code || ''}`
          : t('substitutionsIntro')
      }
      extra={
        <Button onClick={() => navigate(`/education/substitutions/${dayId}`)}>
          {t('backToSubstitutionDay')}
        </Button>
      }
    >
      <Card size="small" style={{ maxWidth: 640 }} loading={detail.isLoading || candidates.isLoading}>
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            cover_staff_link_id: cover?.cover_staff_link_id || undefined,
            cover_room_id: cover?.cover_room_id || undefined,
            notes: cover?.notes || undefined,
            notify: true,
          }}
          onFinish={(values) => assign.mutate(values)}
        >
          <Form.Item name="cover_staff_link_id" label={t('coverTeacher')} rules={[{ required: true }]}>
            <Select
              showSearch
              optionFilterProp="label"
              options={(candidates.data ?? []).map((row) => ({
                value: row.staff_link_id,
                disabled: row.available === false,
                label: (
                  <span>
                    {`${row.employee_code || ''} — ${[row.first_name, row.last_name].filter(Boolean).join(' ')}`.trim()}
                    {row.teaches_subject === 1 || row.teaches_subject === true ? (
                      <Tag color="blue" style={{ marginInlineStart: 8 }}>
                        {t('subjectAffinity')}
                      </Tag>
                    ) : null}
                    {row.available === false ? (
                      <Tag style={{ marginInlineStart: 8 }}>{t('unavailablePeriod')}</Tag>
                    ) : null}
                  </span>
                ),
              }))}
            />
          </Form.Item>
          <Form.Item name="cover_room_id" label={t('coverRoomOptional')}>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              options={(rooms.data ?? []).map((r) => ({
                value: r.id,
                label: `${r.code} — ${r.name}`,
              }))}
            />
          </Form.Item>
          <Form.Item name="notes" label={t('notes')}>
            <Input.TextArea rows={2} maxLength={500} />
          </Form.Item>
          <Form.Item name="notify" valuePropName="checked">
            <Checkbox>{t('notifyCoverTeacher')}</Checkbox>
          </Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={assign.isPending}>
              {t('save')}
            </Button>
            <Button onClick={() => navigate(`/education/substitutions/${dayId}`)}>{t('cancel')}</Button>
          </Space>
        </Form>
      </Card>
    </EducationPageChrome>
  );
}
